import { ChevronUpIcon, CopyIcon, DeleteIcon, SparklesIcon } from './ui/icons';
import { useAiAvailability } from '../hooks/useAiAvailability';
import { AiAvailabilityTooltip } from './ui/AiAvailabilityTooltip';
import { AiCreditTooltip } from './ui/AiCreditTooltip';
import { recordKey } from '../features/collection/multiCollectionIdentity';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { logger } from '../services/logger';
import {
  Box,
  Alert,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { LocationPicker } from '../features/locations/components/LocationPicker';
import { LocationUpdate } from '../features/locations/components/LocationEditorDialog';
import { Location } from '../domain/location';
import { Item } from '../domain/item';
import {
  imageAnalysisService,
  mergeImageKeywords,
  ImageAnalysisAuthority,
  ImageAnalysisIntent,
} from '../services/imageAnalysisService';
import { prefersCameraCapture } from '../utils/device';
import ImagePicker from './ImagePicker';
import { useFeedback } from '../features/feedback/FeedbackProvider';
import { ResponsiveDialog } from './ui/ResponsiveDialog';
import { DialogHeader } from './ui/DialogHeader';
import { QuantityControl } from './ui/QuantityControl';
import { usePhoneLayout } from '../hooks/usePhoneLayout';
import { DialogActions } from './ui/DialogActions';
import { userProfileResolverService } from '../services/userProfileResolverService';
import { formatDateTime } from '../utils/dateFormat';

interface ItemDetailsModalProps {
  tagControl?: React.ReactNode;
  tagsPending?: boolean;
  extraActions?: React.ReactNode;
  conflict?: Item;
  onUseSavedDetails?: () => void;
  analysisAvailable?: boolean;
  open: boolean;
  onClose: () => void;
  selectedItem: Item | null;
  image: string | null;
  keywords: string;
  setKeywords: (keywords: string) => void;
  location: string;
  setLocation: (location: string) => void;
  qty: number | string;
  setQty: (qty: number | string) => void;
  handleUpdate: (itemId: string) => Promise<boolean>;
  handleDeleteFromModal: (itemId: string, imageUrl: string) => void;
  locations: Location[];
  setShowAddLocationModal: (show: boolean) => void;
  handleImageCapture: (file: File) => void;
  handleClearImage: () => Promise<boolean>;
  compressionInProgress: boolean;
  compressionProgress: number;
  handleDuplicate: (item: Item) => void;
  saveLabel?: string;
  locationNotice?: string;
  locationsLoading?: boolean;
  updateLastUsedLocation?: (location: string) => Promise<void>;
  saveLocation: (update: LocationUpdate) => Promise<boolean>;
  deleteLocation: (location: Location) => Promise<boolean>;
  showLocation?: boolean;
  showQuantity?: boolean;
  canManageLocations?: boolean;
  canDuplicate?: boolean;
  showMetadata?: boolean;
  readOnly?: boolean;
  canDelete?: boolean;
  aiAuthority?: ImageAnalysisAuthority;
  pendingImage?: Blob | null;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
}

function ItemMetadata({
  item,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
}: {
  item: Item;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
}) {
  const [editorUsername, setEditorUsername] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const editorId = item.lastEditedBy;
    if (!editorId) {
      setEditorUsername(null);
      return;
    }
    if (currentUserId && editorId === currentUserId && currentUsername) {
      setEditorUsername(currentUsername);
      return;
    }

    if (!collectionId || membershipVersion === undefined) {
      setEditorUsername(null);
      return;
    }

    void userProfileResolverService
      .resolveUsername(collectionId, editorId, membershipVersion)
      .then((resolved) => {
        if (active) setEditorUsername(resolved);
      });

    return () => {
      active = false;
    };
  }, [
    item.lastEditedBy,
    currentUserId,
    currentUsername,
    collectionId,
    membershipVersion,
  ]);

  return (
    <Box
      data-testid="item-metadata"
      sx={{
        pt: 2,
        display: 'grid',
        gap: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Created: {formatDateTime(item.createdAt)}
      </Typography>
      {item.lastEditedAt && (
        <Typography variant="caption" color="text.secondary">
          Last edited: {formatDateTime(item.lastEditedAt)}
          {editorUsername && ` by @${editorUsername}`}
        </Typography>
      )}
    </Box>
  );
}

const ItemDetailsModal: React.FC<ItemDetailsModalProps> = ({
  open,
  tagControl,
  tagsPending = false,
  extraActions,
  conflict,
  onUseSavedDetails,
  analysisAvailable = true,
  onClose,
  selectedItem,
  image,
  keywords,
  setKeywords,
  location,
  setLocation,
  qty,
  setQty,
  handleUpdate,
  handleDeleteFromModal,
  locations,
  setShowAddLocationModal,
  handleImageCapture: parentHandleImageCapture,
  handleClearImage,
  compressionInProgress,
  compressionProgress,
  handleDuplicate,
  saveLabel,
  locationNotice,
  locationsLoading,
  updateLastUsedLocation,
  saveLocation,
  deleteLocation,
  showLocation = true,
  showQuantity = true,
  canManageLocations = true,
  canDuplicate = true,
  showMetadata = true,
  readOnly = false,
  canDelete = !readOnly,
  aiAuthority,
  pendingImage,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
}) => {
  const { confirm, notify } = useFeedback();
  const [isSaving, setIsSaving] = useState(false);
  const [isClearingImage, setIsClearingImage] = useState(false);
  const phoneLayout = usePhoneLayout();
  const phoneLandscape = useMediaQuery(
    '(pointer: coarse) and (orientation: landscape) and (max-height: 599.95px)',
  );
  const stacked = phoneLayout && !phoneLandscape;
  const mediaWidth = phoneLandscape
    ? 'min(40%, calc(100dvh - 160px))'
    : 'min(42%, 360px)';
  const isMobile = prefersCameraCapture();
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisSummary, setAnalysisSummary] = useState('');
  const [suggestions, setSuggestions] = useState<string | null>(null);
  const ai = useAiAvailability(
    open && Boolean(image) && !readOnly && analysisAvailable,
    aiAuthority,
  );
  const intentRef = useRef<ImageAnalysisIntent | null>(null);
  const requestVersion = useRef(0);
  const contextRef = useRef({
    open,
    readOnly,
    aiAuthority,
    selectedItem,
    image,
    pendingImage,
  });
  contextRef.current = {
    open,
    readOnly,
    aiAuthority,
    selectedItem,
    image,
    pendingImage,
  };

  useEffect(() => {
    requestVersion.current += 1;
    intentRef.current = null;
    setSuggestions(null);
    setAnalysisSummary('');
    setAnalysisLoading(false);
  }, [
    open,
    selectedItem?.id,
    selectedItem?.storagePath,
    selectedItem?.collectionId,
    image,
    pendingImage,
    readOnly,
    aiAuthority?.userId,
    aiAuthority?.collectionId,
    aiAuthority?.membershipVersion,
  ]);

  const handleGenerateAiKeywords = async () => {
    if (
      !ai.allowed ||
      !analysisAvailable ||
      isSaving ||
      readOnly ||
      analysisLoading ||
      !aiAuthority ||
      !selectedItem ||
      !image
    )
      return;
    const context = contextRef.current;
    const version = requestVersion.current;
    const active = () =>
      version === requestVersion.current &&
      contextRef.current.open &&
      !contextRef.current.readOnly &&
      contextRef.current.aiAuthority?.userId === aiAuthority.userId &&
      contextRef.current.aiAuthority?.collectionId ===
        aiAuthority.collectionId &&
      contextRef.current.aiAuthority?.membershipVersion ===
        aiAuthority.membershipVersion &&
      contextRef.current.selectedItem?.id === selectedItem.id &&
      contextRef.current.image === context.image &&
      contextRef.current.pendingImage === context.pendingImage;
    setAnalysisLoading(true);
    try {
      if (!intentRef.current) {
        const capability = ai.capability;
        if (!active()) return;
        if (!capability?.allowed) return;
        intentRef.current = {
          operationId: crypto.randomUUID(),
          costVersion: capability.costVersion,
          maxCredits: capability.credits,
        };
      }
      const result = await imageAnalysisService.generate(
        aiAuthority,
        pendingImage
          ? { kind: 'received-bytes', image: pendingImage }
          : { kind: 'saved-item', itemId: selectedItem.id },
        intentRef.current,
      );
      if (!active()) return;
      setSuggestions(
        result.keywords.length ? result.keywords.join(', ') : null,
      );
      setAnalysisSummary(
        result.keywords.length
          ? ''
          : `No useful keywords found.${result.cacheHit ? '' : ` Keyword generation used ${result.credits} credit${result.credits === 1 ? '' : 's'}.`}`,
      );
      intentRef.current = null;
      ai.refresh();
    } catch (error: unknown) {
      if (!active()) return;
      const failure = error as {
        code?: string;
        details?: { code?: string; refunded?: boolean };
      };
      // Transport codes wrap the contract error. Retain the financial identity unless
      // the server proves no dispatch occurred or explicitly confirms a refund.
      if (
        (failure.details?.code || failure.code) === 'confirmation-required' ||
        failure.details?.refunded === true
      ) {
        intentRef.current = null;
        ai.refresh();
      }
      if (
        [
          'role-required',
          'insufficient-credits',
          'collection-unavailable',
        ].includes(failure.details?.code || failure.code || '')
      )
        ai.refresh();
      notify(
        error instanceof Error
          ? error.message
          : 'Keyword generation could not finish. Retry uses the same request.',
        'error',
      );
    } finally {
      if (active()) setAnalysisLoading(false);
    }
  };
  const handleSave = useCallback(async () => {
    if (
      readOnly ||
      isSaving ||
      tagsPending ||
      compressionInProgress ||
      !selectedItem
    )
      return;

    setIsSaving(true);
    try {
      await handleUpdate(selectedItem.id);
    } catch (error) {
      logger.error('Error saving item:', error);
    } finally {
      setIsSaving(false);
    }
  }, [
    readOnly,
    isSaving,
    tagsPending,
    compressionInProgress,
    selectedItem,
    handleUpdate,
  ]);

  // Add keyboard shortcut handler
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === ' ' && selectedItem && !isSaving) {
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedItem, handleSave, isSaving]);

  const handleClearImageClick = async () => {
    if (!image || isClearingImage) return;
    if (
      !(await confirm(
        'Remove this image from the item? The item itself will remain.',
        'Remove image',
      ))
    )
      return;
    setIsClearingImage(true);
    try {
      await handleClearImage();
    } finally {
      setIsClearingImage(false);
    }
  };

  if (!selectedItem) return null;

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreenOnPhone
      aria-labelledby="item-details-title"
    >
      <DialogHeader
        id="item-details-title"
        title="Item details"
        onClose={onClose}
      />
      <Box
        data-testid="item-details-layout"
        sx={{
          p: phoneLayout ? 2 : 3,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: { xs: 2.5, sm: 3 },
        }}
      >
        <Box
          data-testid="item-details-media"
          sx={{
            width: stacked ? '100%' : mediaWidth,
            flexShrink: 0,
            ...(phoneLandscape && { position: 'sticky', top: 0 }),
          }}
        >
          <ImagePicker
            navigationKey="inventory.details.image"
            active={open}
            onImage={parentHandleImageCapture}
            image={image}
            compressionInProgress={compressionInProgress}
            compressionProgress={compressionProgress}
            onClear={
              readOnly
                ? undefined
                : () => {
                    void handleClearImageClick();
                  }
            }
            clearing={isClearingImage}
            readOnly={readOnly || isSaving}
          />

          {showMetadata && !phoneLayout && (
            <ItemMetadata
              item={selectedItem}
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              collectionId={collectionId}
              membershipVersion={membershipVersion}
            />
          )}
        </Box>

        <Box
          data-testid="item-details-editor"
          sx={{
            flex: 1,
            minWidth: 0,
            width: stacked ? '100%' : 'auto',
            display: 'grid',
            alignContent: 'start',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 2.5,
          }}
        >
          {conflict && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={isSaving}
                  onClick={onUseSavedDetails}
                >
                  Use saved details
                </Button>
              }
            >
              Another person changed this item. Review the saved values before
              saving your changes.
              <br />
              Saved description: {conflict.keywords || 'Unnamed'}
              <br />
              Saved location: {conflict.location}; quantity: {conflict.qty};
              tags: {conflict.tagIds?.length || 0}.
              {conflict.image && (
                <>
                  <br />
                  <img
                    src={conflict.image}
                    alt="Currently saved item"
                    style={{
                      maxWidth: 96,
                      maxHeight: 96,
                      objectFit: 'contain',
                    }}
                  />
                </>
              )}
            </Alert>
          )}
          <TextField
            label="Keywords"
            variant="outlined"
            value={keywords}
            placeholder="Unnamed"
            onChange={(e) => setKeywords(e.target.value)}
            disabled={readOnly || isSaving}
            fullWidth
            multiline
            inputProps={{ 'data-testid': 'item-keywords-input' }}
            sx={{
              '& .MuiInputBase-root': {
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
              },
            }}
          />
          {analysisSummary && (
            <Typography role="status">{analysisSummary}</Typography>
          )}
          {suggestions !== null && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <ChevronUpIcon
                aria-hidden="true"
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  justifySelf: 'center',
                }}
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <TextField
                  label="Suggested keywords"
                  value={suggestions}
                  onChange={(event) => setSuggestions(event.target.value)}
                  fullWidth
                  multiline
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                    gap: 1,
                    '& .MuiButton-root': {
                      minWidth: 0,
                      minHeight: 44,
                      borderRadius: 1,
                      px: { xs: 0.75, sm: 1.5 },
                      whiteSpace: 'normal',
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    size="small"
                    disabled={readOnly || isSaving || !suggestions.trim()}
                    onClick={() => {
                      setKeywords(mergeImageKeywords(keywords, suggestions));
                      setSuggestions(null);
                    }}
                  >
                    Add to keywords
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={readOnly || isSaving || !suggestions.trim()}
                    onClick={() => {
                      setKeywords(suggestions);
                      setSuggestions(null);
                    }}
                  >
                    Replace keywords
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ color: 'text.secondary' }}
                    onClick={() => setSuggestions(null)}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          {showLocation && (
            <LocationPicker
              navigationKey="inventory.details.location"
              value={location}
              onChange={setLocation}
              locations={locations}
              label="Location"
              helperText={locationNotice}
              loading={locationsLoading}
              onAdd={() => setShowAddLocationModal(true)}
              onSaveLocation={async (update) => {
                const succeeded = await saveLocation(update);
                if (succeeded && updateLastUsedLocation)
                  await updateLastUsedLocation(
                    update.collectionId ? recordKey(update) : update.name,
                  );
                return succeeded;
              }}
              onDeleteLocation={deleteLocation}
              readOnly={readOnly || isSaving}
              canManageLocations={canManageLocations}
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              collectionId={collectionId}
              membershipVersion={membershipVersion}
            />
          )}

          {tagControl}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            {showQuantity && (
              <QuantityControl
                value={qty}
                onChange={setQty}
                disabled={readOnly || isSaving}
              />
            )}
            {!readOnly &&
              analysisAvailable &&
              aiAuthority &&
              image &&
              (pendingImage ||
                !selectedItem?.storagePath?.endsWith('blank_image.png')) && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                  <AiAvailabilityTooltip
                    reason={
                      ai.reason ||
                      (compressionInProgress
                        ? 'Wait for the photo to finish processing.'
                        : '')
                    }
                  >
                    <Button
                      data-testid="ai-keywords-btn"
                      variant="outlined"
                      onClick={handleGenerateAiKeywords}
                      disabled={
                        !ai.allowed ||
                        isSaving ||
                        analysisLoading ||
                        compressionInProgress ||
                        !aiAuthority
                      }
                      size="small"
                      startIcon={
                        analysisLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <SparklesIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        px: 1,
                        fontSize: '0.75rem',
                        '& .MuiButton-startIcon': { mr: 0.5 },
                        fontWeight: 600,
                      }}
                    >
                      Generate keywords
                    </Button>
                  </AiAvailabilityTooltip>
                  <AiCreditTooltip
                    authority={aiAuthority}
                    freeUses={ai.capability?.freeAiUses}
                  />
                </Box>
              )}
          </Box>
          {showMetadata && phoneLayout && (
            <ItemMetadata
              item={selectedItem}
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              collectionId={collectionId}
              membershipVersion={membershipVersion}
            />
          )}
        </Box>
      </Box>
      <DialogActions
        compact
        data-testid="item-details-actions"
        sx={
          readOnly && !canDelete
            ? { display: 'none' }
            : {
                gap: 1,
                pl: stacked ? 2 : `calc(${mediaWidth} + 56px)`,
                '& .MuiButton-root': { minWidth: 0, whiteSpace: 'nowrap' },
                '& .MuiIconButton-root': {
                  flex: '0 0 44px',
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                },
              }
        }
      >
        {extraActions}
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ flex: 1, display: readOnly ? 'none' : undefined }}
          disabled={isSaving || tagsPending || compressionInProgress}
        >
          {isSaving ? 'Saving...' : saveLabel || 'Update'}
          {!isMobile && !isSaving && (
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                opacity: 0.7,
                fontSize: '0.75rem',
              }}
            >
              (Ctrl+Space)
            </Typography>
          )}
        </Button>
        <IconButton
          aria-label="Duplicate item"
          hidden={readOnly || !canDuplicate}
          disabled={isSaving || tagsPending || compressionInProgress}
          onClick={() => handleDuplicate(selectedItem)}
          sx={{
            display: readOnly || !canDuplicate ? 'none' : undefined,
            color: 'text.secondary',
          }}
        >
          <CopyIcon />
        </IconButton>
        <IconButton
          aria-label="Delete item"
          color="error"
          disabled={
            !canDelete || isSaving || tagsPending || compressionInProgress
          }
          onClick={() =>
            handleDeleteFromModal(selectedItem.id, selectedItem.image)
          }
        >
          <DeleteIcon />
        </IconButton>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ItemDetailsModal;
